import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { UserRole } from '../models/user.model';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';

/**
 * Route guard factory that checks if the user has the required role(s)
 * in their active company.
 *
 * Usage:
 *   { path: 'team', canActivate: [roleGuard('root')], ... }
 *   { path: 'team', canActivate: [roleGuard(['root', 'admin'])], ... }
 *
 * Redirects to /dashboard with a toast if unauthorized.
 */
export function roleGuard(
  requiredRoles: UserRole | UserRole[]
): CanActivateFn {
  return (): Observable<boolean | UrlTree> => {
    const auth = inject(AuthService);
    const router = inject(Router);

    const roles = Array.isArray(requiredRoles)
      ? requiredRoles
      : [requiredRoles];

    const isAuthReady$ = toObservable(auth.isAuthReady);
    const appUser$ = toObservable(auth.appUser);

    return isAuthReady$.pipe(
      filter((ready) => ready === true),
      take(1),
      switchMap(() => {
        if (!auth.isLoggedIn()) {
          return of(router.createUrlTree(['/auth/login']));
        }

        return appUser$.pipe(
          filter((user) => user != null),
          take(1),
          map(() => {
            const userRoles = auth.activeCompanyRoles();
            const hasRequiredRole = userRoles.some((r) => roles.includes(r));

            if (hasRequiredRole) {
              return true;
            }

            // Redirect to dashboard if user lacks required role
            return router.createUrlTree(['/dashboard']);
          })
        );
      })
    );
  };
}
